import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const audiences = [
  {
    label: 'Students',
    title: 'Discover strengths. Choose wisely.',
    description:
      'Personalised psychometric insights help you understand your talents and the paths that fit you best.',
    href: '/app/login',
    cta: 'Start Assessment',
    accent: '#22d3ee',
  },
  {
    label: 'Parents',
    title: 'Replace worry with clarity.',
    description:
      'Data-driven guidance highlights your child\'s strengths so you can support them with confidence.',
    href: '/solutions',
    cta: 'Explore Guidance',
    accent: '#2dd4bf',
  },
  {
    label: 'Institutions',
    title: 'Clarity at scale.',
    description:
      'Turn assessment data into strategic action with evidence-based insights for student success.',
    href: '/solutions',
    cta: 'View Solutions',
    accent: '#fbbf24',
  },
];

const AudienceStrip = () => {
  return (
    <section
      id="who-we-serve"
      className="relative overflow-hidden bg-[#f7f9fb] py-20 sm:py-24 lg:py-28"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(15,39,68,0.12) 1px, transparent 0)',
          backgroundSize: '28px 28px',
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -right-24 top-0 h-80 w-80 rounded-full opacity-30 blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(34,211,238,0.35), transparent 70%)' }}
        aria-hidden
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 mb-14 lg:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65 }}
            viewport={{ once: true }}
            className="lg:col-span-5"
          >
            <p className="text-sm font-semibold tracking-[0.2em] uppercase text-cyan-600 mb-4">
              Who we serve
            </p>
            <h2 className="text-4xl sm:text-5xl lg:text-[3.25rem] font-source text-[#0F2744] leading-[1.1]">
              <span className="italic font-light">One prism.</span>
              <br />
              Three perspectives.
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.1 }}
            viewport={{ once: true }}
            className="lg:col-span-6 lg:col-start-7 self-end text-base sm:text-lg text-slate-600 leading-relaxed font-sans"
          >
            Whether you&apos;re choosing a course, supporting a child, or leading an institution —
            Analytical Prism turns complex assessment data into decisions you can trust.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 md:gap-0 border-t border-[#0F2744]/10">
          {audiences.map((item, index) => (
            <motion.a
              key={item.label}
              href={item.href}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: index * 0.1 }}
              viewport={{ once: true }}
              className={`group relative block px-0 md:px-8 py-10 md:py-12 ${
                index > 0 ? 'md:border-l border-[#0F2744]/10' : ''
              } border-b md:border-b-0 border-[#0F2744]/10 hover:bg-white/70 transition-colors`}
            >
              <span
                className="absolute top-0 left-0 h-[3px] w-0 group-hover:w-full transition-all duration-500"
                style={{ background: item.accent }}
              />
              <p
                className="text-sm font-semibold tracking-[0.18em] uppercase mb-4"
                style={{ color: item.accent === '#fbbf24' ? '#b45309' : '#0891b2' }}
              >
                {item.label}
              </p>
              <h3 className="text-2xl font-source text-[#0F2744] mb-4 leading-snug">
                {item.title}
              </h3>
              <p className="text-slate-600 leading-relaxed font-sans mb-8 text-[15px]">
                {item.description}
              </p>
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#0F2744]">
                {item.cta}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1.5" style={{ color: item.accent === '#fbbf24' ? '#d97706' : '#0891b2' }} />
              </span>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AudienceStrip;
